import {
  Text,
  Badge,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useSlotRecipe,
  Box,
  HStack,
} from "@vygruppen/spor-react";

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { InformationFill18Icon } from "@vygruppen/spor-icon-react";
import { moduleRecipe } from "shared/theme/recipes/module";

export type ModuleSize = "md" | "lg";

export type ModuleProps = {
  title: ReactNode;
  titleIcon?: ReactNode; /** Icon displayed to the left of the title */
  specification?: ReactNode;
  badgeLeft1?: ReactNode;
  badgeLeft2?: ReactNode;
  action1?: ReactNode; /** First action button on the right side of the header */
  action2?: ReactNode; /** Second action button on the right side of the header */
  badgeRight?: ReactNode; /** Badge on the right side of the header */
  infoButtonText?: ReactNode; /** Show information popover button with this text on the upper right corner */
  size?: ModuleSize; /** Controls padding, font size and background. Defaults to "md" */
  scrollable?: boolean;
  children?: ReactNode;
  gridArea?: string;
  className?: string;
};

const ModuleDepthContext = createContext(0);

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el = node?.parentElement ?? null;
  while (el) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

export const Module = ({
  title,
  titleIcon,
  specification,
  badgeLeft1,
  badgeLeft2,
  action1,
  action2,
  badgeRight,
  infoButtonText,
  size = "md",
  scrollable = false,
  children,
  gridArea,
  className,
}: ModuleProps) => {
  const depth = useContext(ModuleDepthContext);
  const recipe = useSlotRecipe({ recipe: moduleRecipe });
  const styles = recipe({ size });

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const root = getScrollParent(sentinel);
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { root, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const hasRightContent = action1 || action2 || badgeRight || infoButtonText;

  const childDepth = depth + 1;

  return (
    <ModuleDepthContext.Provider value={childDepth}>
      <Box
        gridArea={gridArea}
        className={className}
        css={{
          ...styles.root,
          overscrollBehavior: depth === 0 && scrollable ? "none" : undefined,
        }}
        overflowY={scrollable ? "auto" : "hidden"}
        data-group
      >
        <Box ref={sentinelRef} css={{ height: 0 }} aria-hidden />
        <Box
          css={{
            ...styles.header,
            zIndex: 100 - depth,
            borderBottom: "1px solid",
            borderBottomColor: isStuck ? "outline" : "transparent",
          }}
        >
          <HStack gap="1" alignItems="center" flexWrap="nowrap" minW="0">
            {titleIcon}
            <Text variant={size}>{title}</Text>
            {specification && (
              <>
                <Box as="span" opacity="0.4" flexShrink="0" userSelect="none">
                  |
                </Box>
                <Text variant={size}>{specification}</Text>
              </>
            )}
            {/* NOTE The floating variant in the design doesnt exist in Spor? */}
            {badgeLeft1 && (
              <Badge size="md" colorPalette="neutral">
                {badgeLeft1}
              </Badge>
            )}
            {badgeLeft2 && (
              <Badge size="md" colorPalette="neutral">
                {badgeLeft2}
              </Badge>
            )}
          </HStack>
          {hasRightContent && (
            <HStack gap="1" alignItems="center" flexShrink="0">
              {action1}
              {action2}
              {badgeRight && (
                <Badge size="md" colorPalette="neutral">
                  {badgeRight}
                </Badge>
              )}
              {infoButtonText && (
                // NOTE This size is bigger than the one in the design and has a different palette
                <Popover>
                  <PopoverTrigger>
                    <IconButton
                      variant="floating"
                      size="xs"
                      icon={<InformationFill18Icon />}
                      aria-label="Informasjon"
                    />
                  </PopoverTrigger>

                  <PopoverContent>{infoButtonText}</PopoverContent>
                </Popover>
              )}
            </HStack>
          )}
        </Box>
        <Box css={styles.body}>{children}</Box>
      </Box>
    </ModuleDepthContext.Provider>
  );
};
